import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "../api/rest";
import type { FollowSummary } from "../api/types";
import { vanished } from "../lib/signals";
import { useSignals } from "../store/useSignals";

/**
 * Поднятие карточек, висящих на момент открытия вкладки.
 *
 * Сокет отдаёт только то, что произошло ПОСЛЕ подключения (Pub/Sub без истории),
 * поэтому сигнал, поданный за минуту до открытия вкладки, иначе не показался бы
 * вовсе — при том, что кнопки на нём ещё живые. Один запрос при монтировании и
 * редкий повтор: живые карточки приезжают сокетом, а это страховка от разрыва.
 */
const PENDING_REFETCH_MS = 30_000;

/** Сколько часов истории тянуть на страницу сигналов. */
const HISTORY_HOURS = 24;

/**
 * Запас, на который сверка ленты отступает назад от момента запроса.
 *
 * Закрывает расхождение часов браузера и сервера и дорогу самого запроса:
 * сигнал, попавший в эту щель, просто доживёт до следующей сверки, а вот
 * погашенная зря карточка стоила бы окна.
 */
const SWEEP_LAG_MS = 2_000;

/**
 * Живые карточки: REST при старте, дальше сокет.
 *
 * Возвращает признак «первая загрузка не удалась»: молчащая карточка и
 * отсутствие карточек выглядят одинаково, и различить их обязан экран.
 */
export function usePendingSignals(): { failed: boolean } {
  const ingestMany = useSignals((s) => s.ingestMany);
  const drop = useSignals((s) => s.drop);

  const query = useQuery({
    queryKey: ["signals", "pending"],
    // Момент отправки запоминается ЗДЕСЬ: ответ описывает ленту на своё время, и
    // без этой метки сверка ниже гасила бы карточки, родившиеся уже после него.
    queryFn: async () => ({ asked: Date.now(), ...(await api.signals({ pending: true })) }),
    refetchInterval: PENDING_REFETCH_MS,
    // В фоновой вкладке не опрашиваем: карточка живёт две минуты, и оператор,
    // вернувшийся к вкладке, получит её тем же запросом при фокусе.
    refetchIntervalInBackground: false,
    retry: 1,
  });

  useEffect(() => {
    const data = query.data;
    if (!data?.signals) return;
    ingestMany(data.signals);
    // Сверка, а не только дописывание: сообщение о гашении могло не доехать
    // (Pub/Sub без гарантий), и карточка осталась бы висеть с живыми кнопками,
    // отвечающими «сигнал больше не ждёт решения». Читается состояние ПОСЛЕ
    // ingestMany — иначе только что приехавшие карточки погасили бы сами себя.
    const known = Object.values(useSignals.getState().signals);
    drop(vanished(known, data.signals, data.asked - SWEEP_LAG_MS));
  }, [query.data, ingestMany, drop]);

  return { failed: query.isError };
}

/** История сигналов для страницы. */
export function useSignalHistory(hours = HISTORY_HOURS) {
  const ingestMany = useSignals((s) => s.ingestMany);

  const query = useQuery({
    queryKey: ["signals", "history", hours],
    queryFn: () => api.signals({ hours }),
    refetchInterval: PENDING_REFETCH_MS,
    retry: 1,
  });

  useEffect(() => {
    if (query.data?.signals) ingestMany(query.data.signals);
  }, [query.data, ingestMany]);

  return query;
}

/**
 * Сводка двух гипотез.
 *
 * `windowLen` — длительность окна серии в секундах (300, 900) либо 0 «все»;
 * часть ключа запроса по тому же доводу, что и у соседей: срез меняет выборку,
 * и кеш прошлого среза выдал бы чужие числа под новым заголовком.
 */
export function useFollowSummary(hours: number, windowLen = 0) {
  return useQuery<{ summary: FollowSummary; scenarios?: Record<string, FollowSummary> }>({
    queryKey: ["follow-summary", hours, windowLen],
    queryFn: () => api.followSummary(hours, windowLen),
    refetchInterval: PENDING_REFETCH_MS,
    retry: 1,
  });
}

/** Режим копирования, как его выставил оператор. */
export function useFollowMode() {
  return useQuery({
    queryKey: ["follow-mode"],
    queryFn: () => api.followMode(),
    refetchInterval: PENDING_REFETCH_MS,
    retry: 1,
  });
}
